import type { VaultProvider } from '../vaults/fetchVaultsAll'
import type { CounterpartyTerms, TermProfile } from './types'

/**
 * Term profiles — the INVARIANT prose, one entry per lender family × variant.
 *
 * `/lending/latest` returns hundreds of markets per call; inlining the long
 * description and the implications list on every one of them would multiply
 * the payload for text that is identical across every market of a lender. So
 * the per-market sheet carries numbers plus a `profileId`, and the prose lives
 * here once, served by a separate cacheable endpoint.
 *
 * `id` grammar: `<family>.<variant>@v<n>`. Bump the `@v` suffix when the PROSE
 * changes materially — the UI keys its "terms acknowledged" memory on the
 * profileId, so a bump re-arms the disclosure gate for users who already
 * accepted the old wording. That is the intended behaviour, not a side effect.
 */

const P = (p: TermProfile): TermProfile => p

export const TERM_PROFILES: TermProfile[] = [
  // ── Variable-rate pool lenders ───────────────────────────────────────────
  P({
    id: 'pool.variable@v1',
    name: 'Variable-rate pool market',
    family: 'pool',
    supply: {
      description:
        'A shared liquidity pool. Deposits earn interest paid by borrowers, at a rate that moves with utilization. Withdrawals are instant up to the cash left in the pool.',
      implications: [
        'Your yield is not fixed — it rises and falls with borrowing demand.',
        'If utilization reaches 100 %, withdrawals are blocked until borrowers repay or new deposits arrive.',
        'Your deposit is exposed to every collateral asset the market accepts, not just one.',
      ],
    },
    borrow: {
      description:
        'Borrow against collateral at a rate that moves with utilization. The debt accrues continuously and can be repaid at any time.',
      implications: [
        'The rate can change block to block — a cheap borrow can become expensive.',
        'If the collateral value falls past the liquidation threshold, a liquidator repays part of your debt and takes collateral plus a penalty.',
      ],
    },
  }),

  P({
    id: 'aave-v4.spoke@v1',
    name: 'Aave V4 spoke (liquidated to a target health factor)',
    family: 'pool',
    supply: {
      description:
        'A shared liquidity pool on an Aave V4 spoke. Deposits earn interest paid by borrowers of the hub asset, at a rate that moves with utilization. Withdrawals are instant up to the cash left in the pool.',
      implications: [
        'Your yield is not fixed — it rises and falls with borrowing demand.',
        'If utilization reaches 100 %, withdrawals are blocked until borrowers repay or new deposits arrive.',
        'Each spoke is its own market: the same asset on another spoke is a separate position with its own risk parameters.',
      ],
    },
    borrow: {
      description:
        'Borrow against collateral at a rate that moves with utilization. Liquidation here is sized to a target health factor rather than capped at a fixed share of your debt: a liquidator repays whatever it takes to bring the position back up to the target, and their bonus grows the further under water it is.',
      implications: [
        'There is no 50 % close-factor protection — how much of your debt is taken depends on how far the position fell, and a deep enough drop takes all of it.',
        'The published liquidation penalty is the MAXIMUM bonus, charged only once the position is far enough under water; a shallow liquidation costs less.',
        'V4 uses ONE loan-to-value per asset for both borrowing and liquidation — there is no buffer between the LTV you can borrow to and the one that gets you liquidated.',
      ],
    },
  }),

  // ── Fixed-term families ──────────────────────────────────────────────────
  P({
    id: 'exactly.fixed@v1',
    name: 'Exactly fixed-rate pool',
    family: 'exactly',
    supply: {
      description:
        'A fixed-rate deposit into a maturity-dated pool, backed by the floating pool. The rate is locked when you deposit.',
      implications: [
        'Withdrawing before maturity sells the position back at the current curve rate — the haircut worsens as rates rise.',
        'After maturity the deposit stops earning but is never penalised and never expires.',
      ],
    },
    borrow: {
      description:
        'A fixed-rate borrow with a static face value (principal + fee) locked at trade time, under a shared cross-margin risk module.',
      implications: [
        'Repaying early is REBATED, not penalised — but the rebate can be zero when the pool has no unassigned earnings left.',
        'Repaying late accrues a linear penalty of roughly 164 %/yr on the face value, which drives the position toward liquidation on its own.',
        'A liquidator settles at full face value — only you, repaying voluntarily, get the early-repay rebate.',
      ],
    },
  }),
  P({
    id: 'midnight.orderbook@v1',
    name: 'Morpho Midnight order book',
    family: 'midnight',
    supply: {
      description:
        'A fixed-rate lend filled from an order book of maker offers rather than a pool. Interest lives entirely in the entry price: you pay a discount now and are owed the face value at maturity.',
      implications: [
        'Exiting before maturity means selling your units into the bid side at a discount — there may be no bid.',
        'Two ongoing fees are charged to the LENDER side and reduce your net position.',
        'Bad debt is socialised across lenders.',
      ],
    },
    borrow: {
      description:
        'A fixed-rate borrow of zero-coupon units. The debt is a static unit count that accrues nothing; you receive the discounted value now and owe the face at maturity.',
      implications: [
        'Once maturity passes the loan is in default and can be liquidated regardless of health or LTV — being past due IS the trigger.',
        'Over-repaying REVERTS: the repayment must be sized to the exact debt.',
        'Repaying early costs face value with neither fee nor rebate, so there is no saving in paying sooner.',
      ],
    },
  }),
  P({
    id: 'term.auction@v1',
    name: 'Term Finance repo',
    family: 'term',
    supply: {
      description:
        'A fixed-rate, fixed-maturity tri-party repo. Lending happens either in a scheduled sealed-bid auction or by buying repo tokens on the secondary market between rounds.',
      implications: [
        'Lending is possible between auction rounds via the secondary book, even when borrowing is not.',
        'Exiting early means selling the repo token on the secondary market at whatever price is bid.',
      ],
    },
    borrow: {
      description:
        'A repurchase obligation with a static face value, originated only inside a scheduled sealed-bid auction round.',
      implications: [
        'Borrowing is ONLY possible inside an open auction round — most repos sit between rounds and cannot be borrowed even though they quote a rate.',
        'Any rate shown outside an open round is the previous round’s clearing rate: historical, not obtainable.',
        'Repaying early costs the full face value with no rebate, so early repayment is strictly worse than holding to maturity.',
      ],
    },
  }),
  P({
    id: 'termmax.fixed@v1',
    name: 'TermMax fixed-maturity market',
    family: 'termmax',
    supply: {
      description:
        'Lending is buying a zero-coupon bond token (FT) on a fixed-maturity AMM. You pay a discount now and redeem the face value at maturity.',
      implications: [
        'You are NOT principal-protected the way a pool supply is: after maturity, unpaid collateral is distributed pro-rata to all bond holders — you can be settled in collateral instead of the asset you lent.',
        'That loss is socialised across the whole bond supply, so a default anywhere in the market affects you regardless of which loan defaulted.',
        'Exiting early requires a maker order to sell into, and one may not exist.',
      ],
    },
    borrow: {
      description:
        'A fixed-maturity loan represented by an NFT, with a static face-value debt that accrues nothing.',
      implications: [
        'After maturity plus a two-hour liquidation window, unpaid collateral is delivered to lenders.',
        'The debt is a static face value — repaying early does not reduce the amount owed.',
      ],
    },
  }),
  P({
    id: 'teller.pool@v1',
    name: 'Teller V2 pooled loan',
    family: 'teller',
    supply: {
      description:
        'A deposit into a lender-commitment pool that funds fixed-term, fixed-APR loans. There is no price oracle anywhere in this market.',
      implications: [
        'Borrowers are defaulted on TIME, not price — the pool’s protection is collateral seizure after a missed payment, not a margin call.',
        'Withdrawals sit behind a delay.',
      ],
    },
    borrow: {
      description:
        'A fixed-term, fixed-APR loan with escrowed collateral and TIME-based default. There is no oracle and no health factor in the liquidation trigger.',
      implications: [
        'On default a liquidator seizes your ENTIRE escrowed collateral, not just the amount owed — at 50 % LTV that is roughly twice the borrowed value.',
        'The grace period after the payment window can be extremely short — as little as five minutes.',
        'Being over-collateralised does not protect you: a perfectly healthy loan is defaulted for being late.',
        'An upfront origination fee is charged at borrow time.',
      ],
    },
  }),
  P({
    id: 'lista.broker@v1',
    name: 'Lista brokered fixed term',
    family: 'lista',
    borrow: {
      description:
        'A fixed-rate loan fronted by a single market broker over an underlying Morpho-style market. Interest accrues linearly and freezes at maturity.',
      implications: [
        'Repaying early costs roughly half the remaining-term interest as a penalty.',
        'If the loan is not repaid at maturity, a keeper refinances it into the variable position.',
      ],
    },
  }),

  P({
    id: 'fraxlend.pair@v1',
    name: 'Isolated variable-rate pair (single-ratio)',
    family: 'pool',
    supply: {
      description:
        'An isolated two-asset pair. Deposits earn interest paid by the pair\u2019s borrowers at a utilization-driven rate, and the deposit is exposed to exactly ONE collateral asset rather than a whole pool\u2019s worth.',
      implications: [
        'Your yield is not fixed \u2014 it rises and falls with borrowing demand in this pair alone.',
        'Withdrawals are limited to the cash left unborrowed in the pair; at full utilization they are blocked until borrowers repay.',
        'Risk is confined to this one collateral asset, which cuts both ways \u2014 no cross-subsidy, but no diversification either.',
      ],
    },
    borrow: {
      description:
        'Borrow the pair\u2019s asset against its one collateral, at a utilization-driven rate. The debt accrues continuously and can be repaid at any time. The maximum LTV is ALSO the liquidation threshold.',
      implications: [
        'There is NO buffer between the borrow limit and liquidation \u2014 borrowing the maximum leaves the position immediately liquidatable on any adverse price move.',
        'The rate can change block to block, and it depends on how long the pair has sat at its current utilization, not only on the utilization itself.',
        'The collateral price only refreshes when someone interacts with the pair, so a quiet market can value your position off a days-old rate.',
      ],
    },
  }),
  P({
    id: 'llamalend.soft@v1',
    name: 'Curve LlamaLend market',
    family: 'llamalend',
    supply: {
      description:
        'An isolated two-asset lending vault. Borrowers are soft-liquidated inside the market’s own AMM rather than by external liquidators.',
      implications: [
        'Losses are absorbed gradually by the AMM rather than by discrete liquidation events.',
      ],
    },
    borrow: {
      description:
        'Collateral is spread over N price bands inside the market’s AMM and converted into the borrowed token gradually as the price enters that range — with no penalty, and reversibly.',
      implications: [
        'There is no single liquidation price: your position converts across a band RANGE, and converts back if the price recovers.',
        'Soft liquidation costs no penalty — but the band range you get, and your collateral factor, are fixed by the band count you choose at open.',
        'A hard liquidation is still possible in the terminal case, and that one does carry a discount.',
      ],
    },
  }),

  // ── CDP families ─────────────────────────────────────────────────────────
  P({
    id: 'liquity.cdp@v1',
    name: 'Liquity V2 family trove',
    family: 'liquity',
    supply: {
      description:
        'The Stability Pool absorbs liquidated debt in exchange for discounted collateral. It is an earn position, not collateral.',
      implications: [
        'Your deposit is converted into collateral when liquidations occur — the balance is meant to change composition.',
      ],
    },
    borrow: {
      description:
        'A CDP where YOU set the interest rate. A one-off upfront fee is charged at open, and there is a minimum debt size.',
      implications: [
        'Your collateral can be REDEEMED at par while the position is perfectly healthy, if you carry the lowest interest rate on the branch. Raising your rate is the defence — but note a redemption that leaves you under the minimum debt puts the trove in a ZOMBIE state that must be topped back up or closed.',
        'If the branch ever SHUTS DOWN (system collateral ratio below its floor, or an oracle failure), that changes: urgent redemptions then pay the redeemer a 2 % collateral bonus against any trove regardless of rate, so the queue position your rate bought is worth nothing and you are no longer left whole.',
        'You pay a one-off upfront fee equal to roughly a week of interest at open, and again whenever you adjust the rate.',
        'There is a minimum debt size; you cannot leave a position below it.',
      ],
    },
  }),
  P({
    id: 'river.cdp@v1',
    name: 'River (Satoshi) trove',
    family: 'river',
    borrow: {
      description:
        'A CDP with NO ongoing interest. The entire borrowing cost is a one-off decaying mint fee charged at open.',
      implications: [
        'There is no APR — the cost is the mint fee, so a short-held loan is expensive and a long-held one is cheap.',
        'Your collateral can be redeemed at par while the position is perfectly healthy — permissionlessly, by any holder of the stablecoin, whenever it trades below its target.',
        'Redemptions here hit the LOWEST-COLLATERALISED troves first. There is no interest rate to raise (this protocol charges none), so the only defence is adding collateral or repaying.',
        'If you are redeemed, collateral left over above the redeemed debt is NOT returned automatically — you have to claim it from the surplus balance.',
      ],
    },
  }),
  P({
    id: 'inverse.firm@v1',
    name: 'Inverse FiRM market',
    family: 'inverse',
    borrow: {
      description:
        'A CDP where interest is PREPAID by holding DBR tokens. The debt principal is static; you burn one DBR per borrowed dollar per year.',
      implications: [
        'Running out of DBR triggers a permissionless forced top-up at roughly 54.75 %/yr, repeatable every block, and freezes withdrawals.',
        'The debt principal never grows — the cost is the DBR balance burning down.',
        'Only governance-approved contracts may borrow, which blocks leverage and other routed flows.',
      ],
    },
  }),
  P({
    id: 'cooler.mono@v1',
    name: 'Olympus Cooler V2 (MonoCooler)',
    family: 'cooler',
    supply: {
      description:
        'gOHM collateral on a treasury-backed CDP. It earns nothing here — gOHM staking emissions are currently zero — and it backs only your own debt.',
      implications: [
        'Your counterparty is the Olympus treasury itself: there are no external lenders, and collateral seized in a liquidation is unstaked and BURNED.',
      ],
    },
    borrow: {
      description:
        'A perpetual treasury CDP at a fixed protocol-set rate — 0.5 % effective today, governance-settable with a hard 10 % cap. There is NO price-based liquidation: the LTV is a debt-per-gOHM PRICE that only ratchets up, and the sole trigger is your accruing debt outrunning that ratchet (LLTV = OLTV × 1.01).',
      implications: [
        'No market price enters the solvency math: liquidation happens only if your compounding debt crosses the up-only governance LTV ratchet, never because the gOHM price moved. A max-borrowed account needs roughly two years of a frozen ratchet to cross.',
        'If liquidated, your gOHM is unstaked and BURNED — there is no auction and no external lender to make whole; the counterparty is the Olympus treasury.',
        'There is a 1,000 USDS minimum debt: it gates borrows AND partial repays — any action leaving a debt below it reverts (a full repay is always fine).',
        'The position is perpetual — no maturity, no expiry.',
        'The debt token itself is governance-swappable (a new treasury borrower can replace USDS), so read it live rather than assuming USDS.',
      ],
    },
  }),
  P({
    id: 'dss.cdp@v1',
    name: 'Maker-style (dss) CDP ilk',
    // One profile for the whole dss family — Sky (the original MakerDAO) and
    // its forks (USDD 2.0). The prose is identical because the mechanism is:
    // same Vat/Jug/Spot accounting, same governance-set stability fee.
    family: 'dss',
    borrow: {
      description:
        'A Maker-style CDP with a governance-set stability fee and no utilization curve.',
      implications: [
        'The rate is set by governance, not by a market — it can change without any utilization signal.',
      ],
    },
  }),

  P({
    id: 'frankencoin.challenge@v1',
    name: 'Frankencoin position',
    family: 'frankencoin',
    borrow: {
      description:
        'A per-position CDP minting ZCHF. There is no price oracle: you DECLARE the liquidation price yourself, and anyone may challenge it with a Dutch auction.',
      implications: [
        'Liquidation is NOT driven by your health factor. A challenge succeeds when the market price falls below the price YOU declared, regardless of how much you have borrowed — repaying does not reduce that risk, only re-declaring the price does.',
        'You owe more than you received: a 10–40 % reserve contribution is withheld at mint, plus an upfront fee. The debt is not the amount you got.',
        'The position EXPIRES on a fixed date; past it the position can be liquidated.',
      ],
    },
  }),

  P({
    id: 'resupply.cdp@v1',
    name: 'Resupply pair',
    family: 'resupply',
    borrow: {
      description:
        'A CDP minting reUSD against a supply position in ANOTHER lending market, held as a staked ERC-4626 share.',
      implications: [
        'Redemptions are COMMUNAL: collateral is skimmed pro-rata from every borrower in the pair, so being well-collateralised does not exempt you and there is no rate to raise in defence.',
        'Your collateral is a lending position elsewhere — bad debt in that market is bad debt here.',
        'There is a hard minimum position size: a smaller borrow, or a repay that would leave less than it, reverts.',
      ],
    },
  }),

  P({
    id: 'curvance.dual@v1',
    name: 'Curvance isolated market',
    family: 'curvance',
    supply: {
      description:
        'An isolated two-token market on Monad. Each leg is a cToken; a leg with no debt cap is collateral-only and pays no supply interest — its return is the underlying asset\u2019s own yield.',
      implications: [
        'Posting collateral starts a 20-minute hold during which you cannot redeem or transfer your shares.',
        'A collateral-only leg pays 0 % — the yield you earn is whatever the deposited asset earns on its own.',
      ],
    },
    borrow: {
      description:
        'Borrow the market\u2019s other leg against your collateral, with liquidation running from a soft threshold (partial) to a hard one (full).',
      implications: [
        'You cannot repay for 20 MINUTES after opening or topping up — the position cannot be closed, deleveraged or migrated out in that window, and an incoming liquidation cannot be averted by repaying (adding collateral still works).',
        'There is a $10 minimum debt, and it gates the exit as well: a partial repay that would leave less than $10 of debt reverts. Closing in full is always allowed.',
        'Liquidation has TWO thresholds — the headline penalty is the one at the soft threshold, and it scales up continuously to the hard one.',
      ],
    },
  }),

  P({
    id: 'flyingTulip.crossMargin@v1',
    name: 'Flying Tulip cross-margin account',
    family: 'flyingTulip',
    supply: {
      description:
        'One cross-margin account per chain: every asset you deposit backs every asset you borrow, under a single global health check.',
      implications: [
        'The rate shown is interest only \u2014 borrowers pay it and it is shared pro rata with utilization. Flying Tulip also pays an FT token emission on deposits that is NOT included here, so the figure is a floor, not the whole return.',
        'A withdrawal is checked against the same health gate as a borrow: it must leave your account at a health factor of 1.50 or better, so a deposit that is backing debt is not freely withdrawable.',
        'While you hold any debt your account must keep a minimum equity in USD \u2014 $250 on Ethereum, $10 on Sonic \u2014 which can block a withdrawal that would take you under it.',
      ],
    },
    borrow: {
      description:
        'Borrow any listed asset against your whole account. There is no per-market LTV: the requirement is charged on your debt as well as your collateral, so how much you can borrow depends on the PAIR.',
      implications: [
        'Liquidation starts at a health factor below 1.25, NOT below 1.0 \u2014 you are liquidatable a quarter of a point earlier than on an ordinary lending market.',
        'A new borrow must leave you at 1.50 or better, so a position opened at the limit sits only 0.25 of health above liquidation from the moment it opens.',
        'Your account must hold a minimum equity in USD for as long as any debt exists ($250 on Ethereum, $10 on Sonic).',
        'Repayment is never blocked and anyone may repay on your behalf \u2014 there is no cooldown, freeze or queue on the way out.',
      ],
    },
  }),

  P({
    id: 'twyne.credit-delegation@v1',
    name: 'Twyne credit delegation',
    family: 'twyne',
    supply: {
      description:
        'You deposit a receipt token you already hold — an Euler eToken, or a Twyne wrapper around an Aave aToken — and keep earning that token\u2019s own yield while borrowers pay you a fee for the borrowing power you were not using.',
      implications: [
        'Your capital never leaves the underlying lending market; only the right to borrow against it is delegated.',
        'You are FIRST-LOSS on that delegation. Twyne liquidates a failing borrower first and you lose nothing — but if nobody does so in time and Aave or Euler liquidates instead, the shortfall comes out of the delegated credit.',
        'You can withdraw whenever credit is not reserved. A fully utilised credit vault cannot be exited until a borrower releases credit.',
      ],
    },
    borrow: {
      description:
        'Borrow against collateral that stays in Aave or Euler, at a higher liquidation LTV than that market allows on its own, by reserving other lenders\u2019 unused borrowing power.',
      implications: [
        'There is no single liquidation LTV here: you pick one for THIS position, inside the band the market allows, and you can change it at any time. Higher means more credit reserved and more paid for it.',
        'You pay TWO interest legs on different bases — the underlying market\u2019s borrow rate on your debt, plus Twyne\u2019s rate on the RESERVED CREDIT, charged in the collateral asset. The second is small precisely because it applies only to the extra slice.',
        'Liquidation does not auction your collateral. A liquidator takes over your vault and must make it healthy in the same transaction; below the safety line you keep your entire remaining equity, and their cut grows only as the position deteriorates.',
        'If no Twyne liquidator acts in time, the underlying market liquidates the whole position at its own penalty — and the vault is then frozen, so it can only be wound up.',
        'Reserved credit is not yours: it is not withdrawable, and "withdraw everything" means your own collateral, not the vault\u2019s balance.',
      ],
    },
  }),

  P({
    id: 'morpho.blue@v1',
    name: 'Morpho Blue isolated market',
    family: 'morpho-blue',
    supply: {
      description:
        'An isolated two-asset market whose parameters — collateral, oracle, interest-rate model and LLTV — are fixed when the market is created and cannot be changed afterwards.',
      implications: [
        'Nobody can raise this market’s LLTV, swap its interest-rate model or point it at a different collateral. Those are set once, at creation.',
        'What CAN still change: the protocol fee on interest (governance-set, capped at 25 %), and — where the oracle contract behind the fixed oracle address is itself upgradeable — the price it reports. Check the oracle block.',
        'Immutability cuts both ways: a market created with a bad oracle or an aggressive LLTV cannot be fixed either, and nobody can pause it for you.',
      ],
    },
    borrow: {
      description:
        'Borrow against a single collateral in an isolated market whose risk parameters are fixed at creation.',
      implications: [
        'Your liquidation threshold is the LLTV the market was created with and cannot be moved under you.',
        'There is no governance pause and no parameter rescue — the market runs as deployed.',
      ],
    },
    docsUrl: 'https://docs.morpho.org/morpho/concepts/markets',
  }),

  // ── Vaults ───────────────────────────────────────────────────────────────
  P({
    id: 'vault.lending@v1',
    name: 'Curated lending vault',
    family: 'vault',
    supply: {
      description:
        'A curated vault that allocates deposits across underlying lending markets. A curator chooses which markets and how much.',
      implications: [
        'You are exposed to every market the curator allocates into, and the allocation can change.',
        'Withdrawals are limited to the vault’s liquid cash — if allocations are fully utilised, exits queue.',
      ],
    },
  }),
  P({
    id: 'vault.savings@v1',
    name: 'Savings vault',
    family: 'savings',
    supply: {
      description:
        'A yield-bearing wrapper whose share price accrues. There is no borrow side and no utilization.',
      implications: [
        'The exit mechanism, not the APR, is the load-bearing term here — check the cooldown before depositing.',
      ],
    },
  }),
  P({
    id: 'vault.nav@v1',
    name: 'NAV-attested vault',
    family: 'savings-nav',
    supply: {
      description:
        'A vault whose share price is published by an operator from an off-chain net asset value rather than derived from on-chain holdings.',
      implications: [
        'There is no on-chain solvency invariant — the share price is a trusted input, not a computed one.',
        'The published value can be stale; some feeds keep no round history at all.',
      ],
    },
  }),
  P({
    id: 'vault.tranche-junior@v1',
    name: 'Junior tranche',
    family: 'tranche',
    supply: {
      description:
        'The junior slice of a tranched structure. It pays a higher rate precisely because it absorbs losses first.',
      implications: [
        'This is FIRST-LOSS capital — a drawdown hits your position before it touches the senior tranche.',
        'Redemptions can be paused when senior coverage falls below its minimum.',
      ],
    },
  }),
  P({
    id: 'vault.tranche-senior@v1',
    name: 'Senior tranche',
    family: 'tranche',
    supply: {
      description:
        'The senior slice of a tranched structure, protected by junior capital up to the size of that junior slice.',
      implications: [
        'Protection is finite — it ends when the junior tranche is exhausted.',
      ],
    },
  }),
  P({
    id: 'vault.credit@v1',
    name: 'Undercollateralized credit pool',
    family: 'credit',
    supply: {
      description:
        'A pool lending to borrowers who are not fully collateralised on-chain. Repayment depends on off-chain credit arrangements.',
      implications: [
        'There is no on-chain collateral to liquidate if a borrower does not repay.',
        'Exits are delayed or capped by the pool’s own inventory.',
      ],
    },
  }),
  P({
    id: 'vault.fixed-maturity@v1',
    name: 'Fixed-maturity discount bond',
    family: 'fixed-maturity',
    supply: {
      description:
        'A zero-coupon position bought at a discount and redeemed for the underlying at par on a fixed date. The yield is the discount, and it is realized only by holding to maturity.',
      implications: [
        'There is NO deposit or redemption before maturity — you enter and exit by TRADING on an AMM, so both legs move against you with size and need a slippage tolerance.',
        'The quoted liquidity is pool DEPTH, not idle cash waiting to be withdrawn.',
        'The rate is fixed at purchase. Selling early realizes whatever the market pays that block, which can be above or below your entry.',
        'Buying gives up the underlying’s own floating yield for the term — that is the trade, not an extra return on top.',
        'Redemption is 1:1 in the underlying, so the fixed rate itself carries no credit risk — but you are paid in that underlying, and whatever risk it carries (a depeg, a failure of the protocol behind it) passes straight through to you.',
      ],
    },
  }),
  P({
    id: 'vault.lst@v1',
    name: 'Liquid staking token',
    family: 'lst',
    supply: {
      description:
        'A share token over staked capital. The share price accrues with staking rewards; the exit runs through the protocol’s unstaking queue rather than a cash withdrawal.',
      implications: [
        'Zero instant liquidity is normal here, not a warning sign — unstaking is a queue with a protocol-set waiting period.',
        'The fast path out is usually a DEX sale, which trades at a discount to the redemption value when the queue is long.',
      ],
    },
  }),
  P({
    id: 'vault.request@v1',
    name: 'Request / epoch vault',
    family: 'request',
    supply: {
      description:
        'A vault settled in rounds by an operator. Deposits and redemptions are REQUESTS that a keeper fulfils at the next settlement, not same-block transfers.',
      implications: [
        'Your funds are committed from the moment you request, at a price fixed when the round settles — not when you clicked.',
        'Settlement depends on an operator running the round; there is no permissionless path to force it.',
      ],
    },
  }),
  P({
    id: 'vault.strategy@v1',
    name: 'Yield strategy vault',
    family: 'strategy',
    supply: {
      description:
        'A vault routing deposits through one or more automated strategies. The share price reflects whatever those strategies realize, net of a performance fee.',
      implications: [
        'The published rate may be a trailing measurement rather than a forward promise — check the rate window before comparing it with a quoted APR.',
        'Withdrawals are limited to whatever the strategies hold liquid.',
      ],
    },
  }),
  P({
    id: 'vault.trading@v1',
    name: 'Trading / market-making vault',
    family: 'trading',
    supply: {
      description:
        'A pool whose value is the running result of a trading or market-making book. There is no interest rate — the headline number is a realized outcome over a past window.',
      implications: [
        'Your PRINCIPAL is at risk in the ordinary course: the share value can and does fall, and a past return is not a rate.',
        'Deposits are counterparty to traders — the pool profits when they lose and loses when they win.',
      ],
    },
  }),
]

const BY_ID = new Map(TERM_PROFILES.map((p) => [p.id, p]))

export function getTermProfile(id: string): TermProfile | undefined {
  return BY_ID.get(id)
}

/** Fallback used when a family has no dedicated profile yet. */
export const DEFAULT_PROFILE_ID = 'pool.variable@v1'

/**
 * Vault provider → its base profile.
 *
 * EXHAUSTIVE over `VaultProvider` by type, which is the point: adding a
 * provider without deciding what its terms are is a compile error, and a test
 * asserts every entry resolves to a real profile. Pendle reached production
 * before this map existed and inherited savings-vault prose ("check the
 * cooldown") that is wrong in every clause — a `Record<VaultProvider, …>` is
 * what makes that impossible to repeat.
 */
export const VAULT_PROVIDER_PROFILE: Record<VaultProvider, string> = {
  // Curated allocators over underlying lending markets.
  morpho: 'vault.lending@v1',
  lista: 'vault.lending@v1',
  silo: 'vault.lending@v1',
  'euler-earn': 'vault.lending@v1',
  fluid: 'vault.lending@v1',
  gearbox: 'vault.lending@v1',
  'aave-earn': 'vault.lending@v1',
  // A curated 4626 rolling fixed-term positions — the VAULT is perpetual even
  // though the instruments inside it are not.
  termmax: 'vault.lending@v1',
  savings: 'vault.savings@v1',
  lst: 'vault.lst@v1',
  pendle: 'vault.fixed-maturity@v1',
  spectra: 'vault.fixed-maturity@v1',
  lagoon: 'vault.request@v1',
  upshift: 'vault.request@v1',
  yearn: 'vault.strategy@v1',
  gmx: 'vault.trading@v1',
  hypercore: 'vault.trading@v1',
}

/**
 * Profile for a vault row.
 *
 * `solvency` — where a curated classification exists — OVERRIDES the provider
 * default, because the loss waterfall is the term that matters most and it
 * varies WITHIN a provider: Strata's senior and junior tranches are both
 * `savings` rows keyed one address apart, and reading the same "your share
 * price accrues" prose on a first-loss tranche is the single most misleading
 * thing this surface could do.
 */
export function resolveVaultProfileId(
  provider: VaultProvider,
  solvency?: CounterpartyTerms['solvency'],
): string {
  switch (solvency) {
    case 'nav-attested':
      return 'vault.nav@v1'
    case 'tranched-senior':
      return 'vault.tranche-senior@v1'
    case 'tranched-junior':
      return 'vault.tranche-junior@v1'
    case 'undercollateralized':
      return 'vault.credit@v1'
    default:
      return VAULT_PROVIDER_PROFILE[provider] ?? 'vault.savings@v1'
  }
}
